import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ReactComponent as UploadIcon } from './../assets/upload-icon.svg';
import { ReactComponent as Loader } from './../../../projectPost/PublishedProposals/loader.svg';
import { uploadFile } from '../../../../firebase/api/media';
import { useAuth } from '../../../../hooks/useAuth';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import UploadedFile from './uploadedFile/UploadedFile';

import './UploadFiles.css';
import classNames from 'classnames';

const maxFilesLength = 8;
const maxFileSize = 83886080; // 80MB

const UploadFiles = ({ values, setValues, name, postId, errors, touched }) => {
    const [loading, setLoading] = useState(false);
    const {
        currentUser: { uid },
    } = useAuth();
    const inputRef = useRef(null);

    const uploadFilesToBucket = useCallback(
        async files => {
            if (values.files.length + files.length > maxFilesLength) {
                toast.error('You can provide up to 8 files!');
                return;
            } else if (files.find(file => file.size > maxFileSize)) {
                toast.error('Max file size is 80MB!');
                return;
            }
            let newFiles = [];
            for (let file of files) {
                const folderName = `${postId}/${uid}`;
                const {
                    _delegate: {
                        metadata: { fullPath },
                    },
                } = await uploadFile(folderName, file.name, file);
                newFiles.push({
                    name: file.name,
                    path: fullPath,
                });
            }

            setValues(prevValues => ({
                ...prevValues,
                [name]: [...values[name], ...newFiles],
            }));
        },
        [setValues, name, postId, uid, values]
    );

    const handleChangeFiles = useCallback(
        async e => {
            if (!loading) {
                setLoading(true);
                const files = Array.from(e.target.files);
                await uploadFilesToBucket(files);
                setLoading(false);
            }
        },
        [uploadFilesToBucket, loading]
    );

    const handleDragOver = useCallback(e => e.preventDefault(), []);
    const handleDrop = useCallback(
        async e => {
            if (!loading) {
                setLoading(true);
                e.preventDefault();
                const files = Array.from(e.dataTransfer.files);
                await uploadFilesToBucket(files);
                setLoading(false);
            }
        },
        [uploadFilesToBucket, loading]
    );

    const uploadedFiles = useMemo(
        () =>
            values[name].length ? (
                <div className="uploaded-files-wrap">
                    <div className="uploaded-files-header">
                        {values[name].length} files uploaded
                    </div>
                    <ul className="uploaded-files-list">
                        {values[name].map((file, i) => (
                            <UploadedFile
                                key={file.path}
                                file={file}
                                i={i}
                                values={values}
                                setValues={setValues}
                                name={name}
                            />
                        ))}
                    </ul>
                </div>
            ) : null,
        [values, name, setValues]
    );

    const handleStop = useCallback(e => e.stopPropagation(), []);
    const handleOpenDownloadFile = useCallback(() => !loading && inputRef.current.click(), [
        loading,
        inputRef,
    ]);

    const dropzoneClass = classNames({
        dropzone: true,
        'is-error': errors[name] && touched[name],
    });

    return (
        <div className="field-wrapper upload-files">
            <div className="field-header">
                <UploadIcon />
                <div className="header-text">Upload media</div>
            </div>
            <div className="field-subHeader portfolioLinks">
                Upload files up to 8 individual files (80MB max per file)
            </div>
            <div onDragOver={handleDragOver} onDrop={handleDrop} className={dropzoneClass}>
                {loading && (
                    <div className="mask-loader-wrap">
                        <Loader />
                    </div>
                )}
                <UploadIcon />
                <p>
                    Drag and drop files here or{' '}
                    <span onClick={handleOpenDownloadFile}>choose files</span>
                </p>
            </div>
            <input
                multiple
                type="file"
                hidden
                onChange={handleChangeFiles}
                ref={inputRef}
                onClick={handleStop}
            />
            {uploadedFiles}
        </div>
    );
};

UploadFiles.propTypes = {
    name: PropTypes.string.isRequired,
    values: PropTypes.any.isRequired,
    setValues: PropTypes.any.isRequired,
    postId: PropTypes.string.isRequired,
    errors: PropTypes.any.isRequired,
    touched: PropTypes.any.isRequired,
};

export default UploadFiles;
